/* eslint ember/no-classic-classes: off */

import { getOwner } from '@ember/application';
import Service from '@ember/service';
import * as optimizelySDK from '@optimizely/optimizely-sdk/dist/optimizely.browser.umd';
import { Promise, defer } from 'rsvp';
import { assert } from '@ember/debug';
import {
  getConsent,
  OneTrustConsent,
} from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';

const INSTANCE_TIMEOUT_MS = 5000;
const ONETRUST_CONSENT_LEVEL = OneTrustConsent.PERFORMANCE;

/**
 * Optimizely Add-On
 * https://docs.developers.optimizely.com/full-stack/docs/javascript-sdk
 *
 * The Optimizely addon is largely a pass through for what is provided from the Optimizely SDK.
 * Built in are a number of safeguards to ensure the service is initialized properly before events are fired.
 */
export default Service.extend({
  isInitializedCalled: false,
  optClient: null,
  evtQueue: null,
  pendingOnReadyPromises: null,

  /**
   * initialize
   * https://docs.developers.optimizely.com/full-stack/docs/initialize-sdk-javascript
   * Passthrough to Optimizely SDK "createInstance" method.
   * @param {string} sdkKey required
   * @param {string} datafile
   * @param {object} eventDispatcher
   * @param {object} logger
   * @param {object} errorHandler
   * @param {object} userProfileService
   * @param {object} datafileOptions
   * @param {Number} timeout - Timeout in ms to load the datafile. Client will fail to initialize if timeout is exceeded
   * @returns {Promise} Promise rejecting with error, or { success: true }
   */
  async initialize(params = {}) {
    const oneTrustPromise = getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);

    /** The test `Unit | Service | optimizely > sad path > setting a custom timeout when initializing the optimizely service`
     * mocks `getConsent` to return null. This is due to how the test is structured. It needs to call
     * `_getClient` instead of awaiting on `getConsent` first to set up a mocked timer correctly.
     */
    const hasConsent = oneTrustPromise ? await oneTrustPromise : true;

    if (!hasConsent) {
      const sdkRequiredError = new Error(
        'Optimizely: Performance tracking consent was rejected'
      );
      for (const { reject } of this.pendingOnReadyPromises) {
        reject(sdkRequiredError);
      }
      throw sdkRequiredError;
    }

    // ensuring a sdkKey is passed
    if (!params.sdkKey) {
      const sdkRequiredError = new Error('Optimizely: a sdkKey is required');
      for (const { reject } of this.pendingOnReadyPromises) {
        reject(sdkRequiredError);
      }
      throw sdkRequiredError;
    }

    const {
      baseAttrs = {},
      timeout = INSTANCE_TIMEOUT_MS,
      ...optimizelyConfig
    } = params;

    this.set('baseAttrs', baseAttrs);

    return Promise.resolve(this._getClient(optimizelyConfig, timeout))
      .then((optClientInstance) => {
        // setting the optClient
        this.set('optClient', optClientInstance);

        this._dispatchEnqueued();
        for (const { resolve } of this.pendingOnReadyPromises) {
          resolve(this);
        }

        return { success: true };
      })
      .catch((err) => {
        for (const { reject } of this.pendingOnReadyPromises) {
          reject(err);
        }
        throw err;
      })
      .finally(() => {
        this.set('isInitializedCalled', true);
        this.set('pendingOnReadyPromises', []);
      });
  },

  /**
   * onReady
   * Running activate within "then" will guarantee the optimizely service has been initialized
   * @returns {Promise} Promise resolving the optimizely service if successfully initialized. Otherwise, it will reject
   * with an error
   */
  onReady() {
    if (this.isInitializedCalled) {
      return this.optClient
        ? Promise.resolve(this)
        : Promise.reject(new Error('Initialization failed'));
    } else {
      const deferredPromise = defer();
      this.pendingOnReadyPromises.push(deferredPromise);
      return deferredPromise.promise;
    }
  },

  /**
   * activate
   * https://docs.developers.optimizely.com/full-stack/docs/activate-javascript
   * @param  {string} experimentKey
   * @param  {string} userId
   * @param  {object} attributes - Optional
   */
  activate(params) {
    this._validateServiceInitialized('activate');
    const {
      experimentKey,
      userId,
      attributes = {},
    } = this._validateFuncSignature(params, 'activate');

    if (!this.optClient) {
      return null;
    }

    return this.optClient.activate(
      experimentKey,
      userId,
      this._mergeUserAttr(attributes)
    );
  },

  /**
   * getVariation
   * https://docs.developers.optimizely.com/full-stack/docs/get-variation-javascript
   * @param  {string} experimentKey
   * @param  {string} userId
   * @param  {object} attributes - Optional
   */
  getVariation(params) {
    this._validateServiceInitialized('getVariation');
    const {
      experimentKey,
      userId,
      attributes = {},
    } = this._validateFuncSignature(params, 'getVariation');

    if (!this.optClient) {
      return null;
    }

    return this.optClient.getVariation(
      experimentKey,
      userId,
      this._mergeUserAttr(attributes)
    );
  },

  /**
   * setForcedVariation
   * https://docs.developers.optimizely.com/full-stack/docs/set-forced-variation-javascript
   * @param  {string} experimentKey
   * @param  {string} userId
   * @param  {object} variationKey - Optional, can be null to clear the existing experiment-to-variation mapping
   */
  setForcedVariation(params) {
    this._validateServiceInitialized('setForcedVariation');
    const { experimentKey, userId, variationKey } = this._validateFuncSignature(
      params,
      'setForcedVariation'
    );

    if (!this.optClient) {
      return null;
    }

    return this.optClient.setForcedVariation(
      experimentKey,
      userId,
      variationKey
    );
  },

  /**
   * track
   * https://docs.developers.optimizely.com/full-stack/docs/track-javascript
   * @param  {string} eventKey
   * @param  {string} userId
   * @param  {object} attributes - Optional
   * @param  {object} eventTags - Optional
   */
  track(params) {
    const {
      eventKey,
      userId,
      attributes = {},
      eventTags = {},
    } = this._validateFuncSignature(params, 'track', ['eventKey', 'userId']);

    if (this.optClient) {
      // firing tracking event
      this.optClient.track(
        eventKey,
        userId,
        this._mergeUserAttr(attributes),
        eventTags
      );
    } else {
      // pushing event onto queue
      this.set('evtQueue', [...this.evtQueue, params]);
    }
  },

  /**
   * isFeatureEnabled
   * https://docs.developers.optimizely.com/full-stack/docs/is-feature-enabled-javascript
   * @param  {string} featureKey
   * @param  {string} userId
   * @param  {object} attributes - Optional
   */
  isFeatureEnabled(params) {
    this._validateServiceInitialized('isFeatureEnabled');
    const {
      featureKey,
      userId,
      attributes = {},
    } = this._validateFuncSignature(params, 'isFeatureEnabled', [
      'featureKey',
      'userId',
    ]);

    if (!this.optClient) {
      return null;
    }

    return this.optClient.isFeatureEnabled(featureKey, userId, attributes);
  },

  /**
   * close
   * Undocumented - ends Optimizely connection and stops autofetch
   */
  close() {
    this._validateServiceInitialized('close');
    this.optClient.close();

    // reset the optClient to state not sure if we have to do this, but
    // since the Event Queue needed it, thought it would be prudent. mcaputo@
    this.setProperties({
      isInitializedCalled: false,
      optClient: null,
    });
  },

  _getClient(optimizelyConfig, timeout) {
    // prevent re-fetch if we already have the optimizely client
    if (this.optClient) {
      return Promise.resolve(this.optClient);
    }

    const optClientInstance = optimizelySDK.createInstance(optimizelyConfig);

    return optClientInstance
      .onReady({ timeout })
      .then(({ success, reason } = {}) => {
        if (success) {
          return optClientInstance;
        } else {
          throw new Error(reason);
        }
      });
  },

  _dispatchEnqueued() {
    if (this.evtQueue.length > 0) {
      for (const event of this.evtQueue) {
        this.track(event);
      }

      this.set('evtQueue', []);
    }
  },

  _mergeUserAttr(attributeOverrides = {}) {
    return { ...this.baseAttrs, ...attributeOverrides };
  },

  _validateServiceInitialized(method) {
    assert(
      `${method} must be called after client has been instantiated.`,
      this.isInitializedCalled
    );
  },

  _validateFuncSignature(
    params,
    method,
    requiredParams = ['experimentKey', 'userId']
  ) {
    for (const item of requiredParams) {
      if (!params[item]) {
        assert(`${method} must be called with a "${item}"`);
      }
    }

    return params;
  },

  init(...args) {
    // leveraging init lifecycle for set up
    this._super(args);
    this.setProperties({
      baseAttrs: {},
      evtQueue: [],
      pendingOnReadyPromises: [],
    });
  },
});
